import type { SheetRow } from '../types/entities';
import type { SheetName } from '../types/sheets';

export type ExistingRow = { rowNumber: number; values: SheetRow };

export interface GoogleSheetsWriter {
  ensureSheetHeaders(sheetName: SheetName, headers: string[]): Promise<void>;
  getRows(sheetName: SheetName, headers: string[]): Promise<ExistingRow[]>;
  appendRows(sheetName: SheetName, headers: string[], rows: SheetRow[]): Promise<void>;
  updateRows(sheetName: SheetName, headers: string[], rows: ExistingRow[]): Promise<void>;
}

export class DryRunSheetsClient implements GoogleSheetsWriter {
  private memory = new Map<SheetName, ExistingRow[]>();

  async ensureSheetHeaders(sheetName: SheetName): Promise<void> {
    if (!this.memory.has(sheetName)) this.memory.set(sheetName, []);
  }

  async getRows(sheetName: SheetName): Promise<ExistingRow[]> {
    return this.memory.get(sheetName) || [];
  }

  async appendRows(sheetName: SheetName, _headers: string[], rows: SheetRow[]): Promise<void> {
    const existing = this.memory.get(sheetName) || [];
    const nextRows = rows.map((row, index) => ({ rowNumber: existing.length + index + 2, values: row }));
    this.memory.set(sheetName, [...existing, ...nextRows]);
  }

  async updateRows(sheetName: SheetName, _headers: string[], rows: ExistingRow[]): Promise<void> {
    const existing = this.memory.get(sheetName) || [];
    const byNumber = new Map(rows.map((row) => [row.rowNumber, row]));
    this.memory.set(sheetName, existing.map((row) => byNumber.get(row.rowNumber) || row));
  }
}

export class GoogleSheetsClient implements GoogleSheetsWriter {
  constructor(private readonly spreadsheetId?: string) {}

  async ensureSheetHeaders(): Promise<void> {
    this.requireCredentials();
    throw new Error('Google Sheets adapter is isolated for credential wiring. Add service account auth, then implement header setup here.');
  }

  async getRows(): Promise<ExistingRow[]> {
    this.requireCredentials();
    throw new Error('Google Sheets adapter is isolated for credential wiring. Add spreadsheets.values.get here.');
  }

  async appendRows(): Promise<void> {
    this.requireCredentials();
    throw new Error('Google Sheets adapter is isolated for credential wiring. Add spreadsheets.values.append here.');
  }

  async updateRows(): Promise<void> {
    this.requireCredentials();
    throw new Error('Google Sheets adapter is isolated for credential wiring. Add spreadsheets.values.batchUpdate here.');
  }

  private requireCredentials() {
    if (!this.spreadsheetId) throw new Error('Missing GOOGLE_SHEETS_SPREADSHEET_ID. Use dry-run mode or configure Google Sheets credentials.');
  }
}
